"use client"

import type { ReactNode } from "react"
import { useRouter } from "next/navigation"
import { useQueryClient } from "@tanstack/react-query"

import FancyButton from "./FancyButton"
import NavLink from "./NavLink"
import Spinner from "./Spinner"

import useGetAuthentication from "@packages/base/hooks/use-get-authentication"
import useSignout from "@packages/base/hooks/use-signout"
import { LogOut } from "lucide-react"

export default function SignLink({
  children,
  className = "w-full cursor-pointer",
  signinTo = "/auth?sign=signin",
  signinSize,
  signinVariant = "outline",
  signoutTo = "/",
  onSignoutSuccess,
  render,
}: {
  children?: ReactNode
  className?: string
  signinTo?: string
  signinSize?: "sm" | "lg"
  signinVariant?: "default" | "secondary" | "outline"
  /** Where to redirect after signout. Used only when no onSignoutSuccess is provided, or after it runs. */
  signoutTo?: string
  onSignoutSuccess?: () => void
  /** When set, content is controlled by parent. Receives isPending and isAuthenticated to show signin or signout UI. */
  render?: (props: { isPending: boolean; isAuthenticated: boolean }) => ReactNode
}) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { data: authentication, isSuccess } = useGetAuthentication({ redirectIfNotAuthenticated: false }, queryClient)
  const { isAuthenticated } = authentication || {}
  const { isPending, mutate: signout } = useSignout({
    onSuccess: () => {
      onSignoutSuccess?.()
      router.push(signoutTo)
    },
  }, queryClient)

  if (!isSuccess) return null

  if (render) {
    if (!isAuthenticated) {
      return (
        <NavLink to={signinTo} size={signinSize} variant={signinVariant} className={className}>
          {render({ isPending: false, isAuthenticated: false })}
        </NavLink>
      )
    }
    return (
      <button
        className={className}
        disabled={isPending}
        onClick={() => signout()}
        type="button"
      >
        {render({ isPending, isAuthenticated: true })}
      </button>
    )
  }

  if (isAuthenticated) {
    const signoutContent = isPending ? (
      <>
        <Spinner
          className="[&>div]:border-current"
          height={1}
          width={1}
        />
        Déconnexion...
      </>
    ) : (
      <>
        <LogOut className="w-3.5 h-3.5" />
        Se déconnecter
      </>
    )

    return (
      <FancyButton
        className={className}
        disabled={isPending}
        onClick={() => signout()}
        type="button"
        variant="outline"
      >
        {signoutContent}
      </FancyButton>
    )
  }

  return (
    <NavLink 
     className={className}
      to={signinTo} 
    >
      {children ?? "Se connecter"}
    </NavLink>
  )
}
